import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt, faPhone,
    faPaperPlane, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { useScrollAnimation } from '../../helpers/useScrollAnimation';
import './Contact.css';

export const Contact = () => {
    const { t } = useTranslation();
    const sectionRef = useScrollAnimation();
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const scrollToMap = () => {
        document.getElementById('map')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        setError('');
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            const res = await fetch('/api/contact.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                signal: controller.signal,
            });
            clearTimeout(timeout);
            const data = await res.json();
            if (data.success) {
                setSubmitted(true);
                setFormData({ name: '', email: '', message: '' });
                setTimeout(() => setSubmitted(false), 5000);
            } else {
                setError(data.message || t('contactFormError'));
            }
        } catch {
            setError(t('contactFormError'));
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="section section-alt" ref={sectionRef}>
            <div className="container">
                <div className="text-center fade-up">
                    <span className="section-label">{t("contactLabel")}</span>
                    <h2 className="section-title">{t("contactTitle")}</h2>
                    <p className="section-subtitle mx-auto">{t("contactSubtitle")}</p>
                </div>

                <div className="contact-grid fade-up">
                    <button className="contact-card contact-card-btn" onClick={scrollToMap}>
                        <div className="contact-icon">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </div>
                        <h4 className="contact-card-title">{t("contactAddress")}</h4>
                        <p className="contact-card-text">Sretena Vukosavljevića 7</p>
                        <p className="contact-card-text">11000 Beograd, Srbija</p>
                        <span className="contact-card-hint">{t("contactMapTitle")} ↓</span>
                    </button>
                    <div className="contact-card">
                        <div className="contact-icon">
                            <FontAwesomeIcon icon={faPhone} />
                        </div>
                        <h4 className="contact-card-title">{t("contactPhone")}</h4>
                        <a href="tel:+381631240394" className="contact-link">
                            +381 63 124 0394
                        </a>
                    </div>
                </div>

                <div className="contact-form-section fade-up">
                    <h3 className="contact-form-title">{t("contactFormTitle")}</h3>
                    <p className="contact-form-subtitle">{t("contactFormSubtitle")}</p>

                    {submitted ? (
                        <div className="form-success">
                            <FontAwesomeIcon icon={faCheckCircle} className="form-success-icon" />
                            <p>{t("contactFormSuccess")}</p>
                        </div>
                    ) : (
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder={t("contactFormName")}
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder={t("contactFormEmail")}
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <textarea
                                    name="message"
                                    placeholder={t("contactFormMessage")}
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            {error && <p className="form-error">{error}</p>}
                            <button type="submit" className="form-submit-btn" disabled={sending}>
                                <FontAwesomeIcon icon={faPaperPlane} />
                                <span>{sending ? t("contactFormSending") : t("contactFormSend")}</span>
                            </button>
                        </form>
                    )}
                </div>

                <div className="contact-map fade-up" id="map">
                    <h3 className="contact-map-title">{t("contactMapTitle")}</h3>
                    <div className="map-wrapper">
                        <iframe
                            title="Fibox lokacija"
                            src="https://maps.google.com/maps?q=Sretena+Vukosavljevica+7+Beograd+Serbia&t=&z=15&ie=UTF8&iwloc=&output=embed"
                            width="100%"
                            height="420"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};
